import type { ViewNode } from "pincerjs";
import type { Design, Engine } from "../engine/engine";
import { h } from "../engine/dom";
import { control, heading } from "../engine/generic";

/*
pincerweb's designs: how particular nodes look, picked by where they are in the tree. The first that matches wins;
anything else is drawn generically, in place (developer mode lists those: they're the designs still to do).

Most designs only name a node and let the stylesheet do the work (`styled`). The rest draw their own markup. A design
that draws everything under its node (`whole`) is redrawn when anything under it changes, so it never holds a text box.

Static words here (the tagline, "Copy") are the shopfront's, in English for now; everything from the tree is already
translated.
*/

type Match = (node: ViewNode, engine: Engine) => boolean;

// Matching by place in the tree
const isKey = (key: string): Match => (node) => node.key === key;
const parent = (key: string): Match => (node, engine) => engine.node(node.parentKey)?.key === key;
const childText = (engine: Engine, node: ViewNode, key: string) => engine.child(node, key)?.wording;

// The button beside a text box, if it has one (a message and Send, a new pincer's name and Create)
function buttonBeside(node: ViewNode, engine: Engine): ViewNode | undefined {
  const parentNode = engine.node(node.parentKey);
  if (parentNode === undefined || node.control !== "Input") return undefined;
  const buttons = engine.children(parentNode).filter((sibling) => sibling.control === "Button");
  return buttons.length === 1 ? buttons[0] : undefined;
}

// A heading at the node's level in the tree (so the page's outline stays in order), with a class
function headingFor(node: ViewNode, className: string, ...inner: (Node | string)[]): HTMLElement {
  const element = heading(node, ...inner);
  element.classList.add(className);
  return element;
}

// Drawn as usual, under a name for the stylesheet and developer mode
function styled(name: string, matches: Match): Design {
  return { name, matches, draw: (node, main, engine) => engine.generic(node, main) };
}

// A circle with someone's initials, coloured by their name: no photos until there are photos (event_space)
function avatar(name: string): HTMLElement {
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map((word) => [...word][0]).join("").toUpperCase() || "?";
  let hash = 0;
  for (const character of name) hash = (hash * 31 + character.codePointAt(0)!) >>> 0;
  return h("span", { class: "avatar", "aria-hidden": "true", style: `--avatar-hue: ${hash % 360}` }, initials);
}

export const designs: Design[] = [

  // A text box with its button: one step, not two
  {
    name: "boxAndButton",
    matches: (node, engine) => buttonBeside(node, engine) !== undefined,
    draw: (node, main, engine) => { main.append(control(node, engine, { then: buttonBeside(node, engine) })); },
  },

  // Something the person is asked for (their name, the code we sent): it stands out
  styled("ask", (node, engine) => node.prominence === "Primary" && node.depth > 1 && engine.children(node).some((child) => child.control != null)),

  // The page's heading is the brand
  {
    name: "brand",
    matches: (node) => node.parentKey == null,
    draw: (node, main) => {
      main.append(h("h1", { class: "brand", id: `${node.compoundKey}-label` },
        h("img", { src: "images/crab-75.png", alt: "", width: "50", height: "38" }),
        h("span", {}, node.wording),
      ));
    },
  },

  // Signed out: the shopfront
  {
    name: "getStarted",
    matches: (node) => node.key === "getStarted" && node.depth === 1,
    draw: (node, main, engine) => {
      main.append(
        h("div", { class: "hero" },
          h("img", { src: "images/crab-150.png", alt: "", width: "200", height: "150" }),
          h("p", { class: "tagline" }, "Find a time that works for everyone, and plan it together."),
        ),
      );
      engine.generic(node, main);
    },
  },

  // Your pincers, as cards: the whole card opens the pincer
  {
    name: "pincerCard",
    whole: true,
    matches: (node, engine) => parent("summaries")(node, engine) && node.key !== "newPincer" && !node.isOpener,
    draw: (node, main, engine) => {
      const load = engine.child(node, "loadPincer");
      const content = [avatar(node.wording), h("span", { class: "cardText" },
        h("strong", {}, node.wording),
        childText(engine, node, "summary") ? h("span", { class: "muted" }, childText(engine, node, "summary")!) : null,
      )];
      if (load !== undefined) {
        const button = h("button", { type: "button", class: "card", id: `${load.compoundKey}-control` }, ...content);
        button.addEventListener("click", () => engine.perform(load.compoundKey, null));
        main.append(button);
      }
      else {
        main.append(h("div", { class: "card current", "aria-current": "page" }, ...content));
      }
    },
  },
  styled("newPincer", isKey("newPincer")),
  styled("summaries", (node) => node.key === "summaries" && node.depth === 1),

  // The pincer itself
  {
    name: "pincerTitle",
    matches: (node) => node.key === "pincer" && node.depth === 1,
    draw: (node, main) => { main.append(headingFor(node, "title", node.wording)); },
  },
  styled("description", (node, engine) => node.key === "description" && parent("pincer")(node, engine)),

  // When it's happening, once it's chosen
  {
    name: "startTime",
    whole: true,
    matches: (node) => node.key === "startTime",
    draw: (node, main, engine) => {
      main.append(h("div", { class: "banner" },
        headingFor(node, "whenLabel", node.wording),
        h("p", { class: "when" }, childText(engine, node, "value") ?? ""),
      ));
    },
  },

  // Suggested times: the grid comes from the generic drawing; each suggestion is a card
  styled("suggestions", isKey("suggestions")),
  {
    name: "suggestion",
    whole: true,
    matches: parent("suggestions"),
    draw: (node, main, engine) => {
      const confirm = engine.child(node, "confirmCandidate");
      main.append(h("div", { class: "card suggestion" },
        headingFor(node, "time", node.wording),
        h("p", { class: "muted" }, childText(engine, node, "du") ?? ""),
        childText(engine, node, "pt") ? h("p", {}, childText(engine, node, "pt")!) : null,
        childText(engine, node, "un") ? h("p", { class: "muted" }, childText(engine, node, "un")!) : null,
        confirm !== undefined ? control(confirm, engine) : null,
      ));
    },
  },

  // Chat, as speech bubbles
  styled("chats", isKey("chats")),
  styled("earlier", isKey("earlier")),
  {
    name: "message",
    whole: true,
    matches: (node, engine) => (parent("chats")(node, engine) || parent("earlier")(node, engine)) && node.childKeys.length === 1 && engine.child(node, "wording") !== undefined,
    draw: (node, main, engine) => {
      main.append(h("div", { class: "message" },
        avatar(node.wording),
        h("div", { class: "bubble" },
          h("span", { class: "who" }, node.wording),
          h("span", { class: "said" }, childText(engine, node, "wording") ?? ""),
        ),
      ));
    },
  },
  styled("composer", isKey("addChatMessage")),

  // Fields: a label, the saved value, and a way to change it (see Node.field in the app)
  styled("field", (node, engine) => node.control == null && node.open == null && engine.child(node, "value") !== undefined && node.childKeys.length <= 3),
  {
    name: "shareableLink",
    matches: (node, engine) => node.key === "value" && parent("shareableLink")(node, engine),
    draw: (node, main) => {
      const copy = h("button", { type: "button", class: "small" }, "Copy");
      copy.addEventListener("click", () => {
        navigator.clipboard?.writeText(node.wording).then(() => { copy.textContent = "Copied"; }, () => {});
      });
      main.append(h("p", { id: `${node.compoundKey}-label`, class: "link" }, h("a", { href: node.wording }, node.wording), " ", copy));
    },
  },
  styled("value", isKey("value")),
  styled("change", (node) => node.key === "change" && node.open != null),

  // People
  {
    name: "participant",
    matches: parent("participants"),
    draw: (node, main) => {
      main.append(headingFor(node, "person", avatar(node.wording), h("span", {}, node.wording)));
    },
  },
  styled("participants", isKey("participants")),

  // Questions to answer
  styled("questions", (node, engine) => node.key === "questions" && parent("pincer")(node, engine)),

  // Settings, at the edges
  styled("settings", isKey("settings")),

  // Controls look the same wherever they are (the stylesheet does it)
  styled("button", (node) => node.control === "Button"),
  styled("box", (node) => node.control != null),

  // Messages to the person
  styled("problem", (node) => node.showAs === "Problem"),
  styled("helpText", (node) => node.showAs === "HelpText"),
  styled("loading", (node) => node.showAs === "Loading"),
];
